import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Header from '@/components/layout/Header';
import { DashboardContentContainer } from '@/components/layout/DashboardContentContainer';
import { Search, CheckCircle, XCircle, QrCode, Camera, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import QRCode from 'qrcode';
// QR Scanner library - uncomment when ready to use
// import { Scanner as QrScanner } from '@yudiel/react-qr-scanner';
import { showSuccess, showError } from '@/lib/swal';
import { RSVP } from '@/types';
import { EventHeaderCredits } from '@/components/event/EventHeaderCredits';
import { mapActiveRsvpsFromDocs } from '@/lib/event-utils';

export default function CheckInPage() {
  const { unitId, eventId } = useParams<{ unitId: string; eventId: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [rsvps, setRsvps] = useState<RSVP[]>([]);
  const [showScanner, setShowScanner] = useState(false);
  const [selectedRsvp, setSelectedRsvp] = useState<RSVP | null>(null);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>('');

  useEffect(() => {
    const fetchRSVPs = async () => {
      if (!unitId || !eventId) {
        setLoading(false);
        return;
      }

      try {
        const rsvpsSnapshot = await getDocs(
          collection(db, 'units', unitId, 'events', eventId, 'rsvps')
        );
        setRsvps(mapActiveRsvpsFromDocs(rsvpsSnapshot.docs));
      } catch (error) {
        console.error('Error fetching RSVPs:', error);
        showError('Failed to load guest list');
      } finally {
        setLoading(false);
      }
    };

    fetchRSVPs();
  }, [unitId, eventId]);

  const checkedInCount = rsvps.filter((r) => r.checkedIn).length;
  const totalGuests = rsvps.length;

  const filteredRSVPs = rsvps.filter((rsvp) =>
    rsvp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCheckIn = async (rsvpId: string) => {
    if (!unitId || !eventId) return;

    const rsvp = rsvps.find((candidate) => candidate.id === rsvpId);
    if (rsvp?.apologies === true || rsvp?.status === 'declined') {
      showError('Cannot check in someone who has given their apologies.');
      return;
    }
    if (rsvp?.status === 'cancelled') {
      showError('Cannot check in someone whose booking has been cancelled.');
      return;
    }

    try {
      await updateDoc(
        doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId),
        {
          checkedIn: true,
          checkInTime: serverTimestamp(),
        }
      );

      // Update local state
      setRsvps(
        rsvps.map((r) =>
          r.id === rsvpId ? { ...r, checkedIn: true, checkInTime: new Date() } : r
        )
      );

      showSuccess('Guest checked in successfully!');
    } catch (error) {
      console.error('Error checking in guest:', error);
      showError('Failed to check in guest');
    }
  };

  const handleUndoCheckIn = async (rsvpId: string) => {
    if (!unitId || !eventId) return;

    try {
      await updateDoc(
        doc(db, 'units', unitId, 'events', eventId, 'rsvps', rsvpId),
        {
          checkedIn: false,
          checkInTime: null,
        }
      );

      setRsvps(
        rsvps.map((r) =>
          r.id === rsvpId ? { ...r, checkedIn: false, checkInTime: undefined } : r
        )
      );

      showSuccess('Check-in undone');
    } catch (error) {
      console.error('Error undoing check-in:', error);
      showError('Failed to undo check-in');
    }
  };

  const handleShowQRCode = async (rsvp: RSVP) => {
    setSelectedRsvp(rsvp);
    
    // Generate QR code containing RSVP ID
    const qrData = JSON.stringify({
      unitId,
      eventId,
      rsvpId: rsvp.id,
      name: rsvp.name,
    });

    try {
      const qrUrl = await QRCode.toDataURL(qrData, {
        width: 300,
        margin: 2,
        color: {
          dark: '#1E3A5F', // Navy
          light: '#FFFFFF',
        },
      });
      setQrCodeUrl(qrUrl);
    } catch (error) {
      console.error('Error generating QR code:', error);
      showError('Failed to generate QR code');
    }
  };

  const handleQRScan = async (result: string) => {
    try {
      const data = JSON.parse(result);
      if (data.rsvpId && data.eventId === eventId) {
        await handleCheckIn(data.rsvpId);
        setShowScanner(false);
      }
    } catch (error) {
      console.error('Error processing QR code:', error);
      showError('Invalid QR code');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-background text-foreground">
        <Header />
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mb-4 h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto" />
            <p className="text-muted-foreground">Loading guest list...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <Header />
      <main className="flex-1 w-full min-w-0 py-6 sm:py-8">
        <DashboardContentContainer>
          <div className="mb-6 sm:mb-8 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="ghost" onClick={() => navigate(`/event/${eventId}`)}>
              ← Back to Event
            </Button>
            <EventHeaderCredits unitId={unitId} />
          </div>

          <div className="mb-6 flex flex-col gap-4 sm:mb-8 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0">
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">Guest Check-In</h1>
              <p className="text-muted-foreground">Mark guests as they arrive at the event</p>
            </div>
            <Button
              className="w-full shrink-0 sm:w-auto"
              onClick={() => setShowScanner(!showScanner)}
              variant={showScanner ? 'destructive' : 'default'}
            >
              {showScanner ? (
                <>
                  <X className="mr-2 h-4 w-4" />
                  Close Scanner
                </>
              ) : (
                <>
                  <Camera className="mr-2 h-4 w-4" />
                  Scan QR Code
                </>
              )}
            </Button>
          </div>

          {/* QR Scanner */}
          {showScanner && (
            <Card className="mb-8">
              <CardContent className="pt-6">
                <div className="relative">
                  {/* QR Scanner component - requires camera permissions */}
                  <div className="bg-gray-900 rounded-lg p-8 text-center">
                    <Camera className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                    <p className="text-white mb-2">QR Code Scanner</p>
                    <p className="text-sm text-gray-400">
                      QR code scanning requires camera access. In production, use a mobile-optimized scanner library like @yudiel/react-qr-scanner.
                    </p>
                    <p className="text-xs text-gray-500 mt-4">
                      For now, use the QR code icon next to each guest to generate their check-in code.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statistics */}
          <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Checked In</CardDescription>
                <CardTitle className="text-3xl text-emerald-600 dark:text-emerald-400">
                  {checkedInCount}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Not Yet Arrived</CardDescription>
                <CardTitle className="text-3xl text-orange-600 dark:text-orange-400">
                  {totalGuests - checkedInCount}
                </CardTitle>
              </CardHeader>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardDescription>Completion Rate</CardDescription>
                <CardTitle className="text-3xl text-blue-600 dark:text-blue-400">
                  {totalGuests > 0 ? Math.round((checkedInCount / totalGuests) * 100) : 0}%
                </CardTitle>
              </CardHeader>
            </Card>
          </div>

          {/* Search */}
          <Card className="mb-6">
            <CardContent className="pt-6">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-5 w-5 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Search guests by name..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardContent>
          </Card>

          {/* Guest List */}
          <Card>
            <CardHeader>
              <CardTitle>Guest List</CardTitle>
              <CardDescription>Click to check in guests or scan their QR codes</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {filteredRSVPs.map((rsvp) => (
                  <div
                    key={rsvp.id}
                    className={`flex flex-col gap-3 rounded-lg border p-4 transition-colors sm:flex-row sm:items-center sm:justify-between ${
                      rsvp.checkedIn
                        ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-500/45 dark:bg-emerald-950/55'
                        : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <div className="flex flex-1 items-start gap-3 sm:items-center sm:space-x-4">
                      {rsvp.checkedIn ? (
                        <CheckCircle className="h-6 w-6 shrink-0 text-emerald-600 dark:text-emerald-400" />
                      ) : (
                        <div className="h-6 w-6 shrink-0 rounded-full border-2 border-muted-foreground" />
                      )}
                      <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row sm:items-center sm:gap-2">
                        <div className="min-w-0">
                          <div className="break-words font-semibold text-foreground">{rsvp.name}</div>
                          <div className="text-sm text-muted-foreground">
                            {rsvp.rank}
                            {rsvp.checkInTime && (
                              <span> • Checked in at {rsvp.checkInTime.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-1.5">
                          {rsvp.apologies === true && (
                            <Badge variant="secondary">Apologies</Badge>
                          )}
                          {rsvp.status === 'declined' && (
                            <Badge variant="destructive">Declined</Badge>
                          )}
                          {rsvp.status === 'cancelled' && (
                            <Badge variant="destructive">Cancelled</Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex w-full items-center justify-end gap-2 sm:w-auto sm:justify-end">
                      {!rsvp.checkedIn && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleShowQRCode(rsvp)}
                          title="Show QR Code"
                        >
                          <QrCode className="h-4 w-4" />
                        </Button>
                      )}
                      <Button
                        className="min-w-[6rem]"
                        variant={rsvp.checkedIn ? 'outline' : 'default'}
                        size="sm"
                        disabled={!rsvp.checkedIn && (rsvp.apologies === true || rsvp.status === 'declined' || rsvp.status === 'cancelled')}
                        onClick={() => rsvp.checkedIn ? handleUndoCheckIn(rsvp.id) : handleCheckIn(rsvp.id)}
                      >
                        {rsvp.checkedIn
                          ? 'Undo'
                          : rsvp.status === 'cancelled'
                            ? 'Cancelled'
                            : (rsvp.apologies === true || rsvp.status === 'declined')
                              ? 'Apologies'
                              : 'Check In'}
                      </Button>
                    </div>
                  </div>
                ))}

                {filteredRSVPs.length === 0 && searchTerm && (
                  <div className="py-8 text-center text-muted-foreground">
                    <XCircle className="mx-auto mb-2 h-8 w-8" />
                    <p>No guests found matching "{searchTerm}"</p>
                  </div>
                )}

                {rsvps.length === 0 && (
                  <div className="py-8 text-center text-muted-foreground">
                    No RSVPs yet for this event
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </DashboardContentContainer>
      </main>

      {/* QR Code Modal */}
      {selectedRsvp && qrCodeUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={() => {
            setSelectedRsvp(null);
            setQrCodeUrl('');
          }}
        >
          <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>QR Code for Check-In</CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSelectedRsvp(null);
                    setQrCodeUrl('');
                  }}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
              <CardDescription>{selectedRsvp.name} - {selectedRsvp.rank}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col items-center">
                <img src={qrCodeUrl} alt="QR Code" className="mb-4" />
                <p className="text-sm text-muted-foreground text-center mb-4">
                  Guest can show this QR code at check-in for instant verification
                </p>
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.download = `${selectedRsvp.name}-QR-Code.png`;
                    link.href = qrCodeUrl;
                    link.click();
                  }}
                >
                  <QrCode className="mr-2 h-4 w-4" />
                  Download QR Code
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

